// Import the functions you need from the SDKs you need
import { getAnalytics, setAnalyticsCollectionEnabled } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth, sendPasswordResetEmail } from "firebase/auth";
import {
  connectFirestoreEmulator,
  Firestore,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
} from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { getMessaging, isSupported as isMessagingSupported, Messaging } from "firebase/messaging";
import { FirebasePerformance, getPerformance } from "firebase/performance";
import { getRemoteConfig, isSupported } from "firebase/remote-config";
import { connectStorageEmulator, getStorage } from "firebase/storage";

const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS ?? false;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const perf: FirebasePerformance = getPerformance(app);

let db: Firestore;
if (import.meta.env.DEV) {
  db = getFirestore(app);
} else {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) }),
  });
}
const auth = getAuth(app);
const storage = getStorage(app);
const functions = getFunctions(app);
let _messaging: Messaging | null = null;

async function getMessagingInstance(): Promise<Messaging | null> {
  if (_messaging !== null) {
    return _messaging;
  }
  const supported = await isMessagingSupported();
  if (supported) {
    _messaging = getMessaging(app);
    return _messaging;
  }
  return null;
}

const remoteConfig = async () => ((await isSupported()) ? getRemoteConfig(app) : null);

setAnalyticsCollectionEnabled(analytics, !import.meta.env.DEV);

if (USE_EMULATORS && import.meta.env.DEV) {
  connectFirestoreEmulator(db, "localhost", 8080);
  connectAuthEmulator(auth, "http://localhost:9099");
  connectStorageEmulator(storage, "localhost", 9199);
  connectFunctionsEmulator(functions, "localhost", 5001);
}

const sendPasswordReset = (email: string) => {
  return sendPasswordResetEmail(auth, email);
};

export {
  analytics,
  auth,
  db,
  functions,
  getMessagingInstance,
  isMessagingSupported,
  perf,
  remoteConfig,
  sendPasswordReset,
  storage,
};
